import { useMemo } from 'react';

import { createUserGroupsFilter } from 'features/search/utils';
import { useCommunityContext } from 'providers';
import { GroupDetailsFragment, GroupFilter, useUserJoinedGroupsQuery } from 'shared/graphql';

export type UseUserJoinedGroupsOptions = Parameters<typeof useUserJoinedGroupsQuery>[0] & {
  /**
   * If `true`, only the groups that belong to the current school will be returned.
   * @default false
   */
  currentSchoolOnly?: boolean;

  variables: {
    filter?: GroupFilter;
    /** The ID of the user whose joined groups are to be fetched. */
    user: string;
  };
};

/**
 * Custom hook to fetch user joined groups with optional filtering based on the current school.
 *
 * @param {UseUserJoinedGroupsOptions} options - Configuration options for the hook.
 * @returns An object containing the user joined groups, loading state, and refetch function.
 */
export function useUserJoinedGroups(options: UseUserJoinedGroupsOptions) {
  const { selectedCommunity } = useCommunityContext();

  const currentSchoolOnly = options.currentSchoolOnly ?? false;

  const filter = useMemo(() => {
    return createUserGroupsFilter({
      initial: options?.variables?.filter,
      school: currentSchoolOnly ? selectedCommunity : undefined,
    });
  }, [currentSchoolOnly, selectedCommunity, options.variables?.filter]);

  const {
    data: response,
    loading,
    refetch,
    fetchMore,
  } = useUserJoinedGroupsQuery({
    ...options,

    skip: !options.variables?.user || !selectedCommunity,

    variables: {
      user: options.variables.user,
      filter,
    },
  });

  const data: GroupDetailsFragment[] = response?.joinedGroups?.items ?? [];
  const count = response?.joinedGroups?.count ?? 0;

  return {
    data,
    count,
    loading,
    refetch,
    fetchMore,
  };
}
